import { Component, computed, input, output } from '@angular/core';
import { Course, DurationUnitEnum } from '@lx/shared/data-access/types';
import { LocalizeAndPluralizePeriodPipe, UnitLabelPipe } from '@lx/shared/ui/pipes';
import { OnlineCourseDetails } from '../../types';
import { CourseSelectionCardComponent } from '../course-selection-card/course-selection-card.component';

/**
 * Course Intensity Selector Component
 *
 * Displays available courses as option cards and allows users to:
 * - Select a course from the list
 * - Open the course comparison dialog
 *
 * This component is presentation-only and delegates all logic to the parent.
 */
@Component({
  standalone: true,
  selector: 'lx-course-intensity-selector',
  templateUrl: './course-intensity-selector.component.html',
  styleUrls: ['./course-intensity-selector.component.scss'],
  imports: [CourseSelectionCardComponent, LocalizeAndPluralizePeriodPipe, UnitLabelPipe],
})
export class CourseIntensitySelectorComponent {
  // Inputs
  readonly availableCourses = input.required<Course[]>();
  readonly selectedCourse = input<OnlineCourseDetails | undefined>(undefined);

  // Outputs
  readonly courseSelected = output<Course>();
  readonly compareCoursesClicked = output<void>();

  /**
   * Computed signal to find the currently selected course from the available courses list
   */
  protected readonly selectedCourseFromList = computed(() => {
    const selectedCode = this.selectedCourse()?.code;
    return this.coursesWithHours().find((c) => c.code === selectedCode);
  });

  /**
   * Computed signal that transforms courses to include hours for display
   * Calculated once when availableCourses changes, not on every change detection cycle
   */
  protected readonly coursesWithHours = computed(() => {
    return this.availableCourses().map((course) => ({
      ...course,
      lessonHours: course?.lesson?.durationMinutes ? course.lesson.durationMinutes / 60 : 0,
    }));
  });

  protected readonly DurationUnitEnum = DurationUnitEnum;

  /**
   * Handles course selection from option cards
   */
  protected onSelectCourse(course: Course): void {
    if (!course) {
      return;
    }
    this.courseSelected.emit(course);
  }

  /**
   * Handles compare courses link click
   */
  protected onCompareCourses(): void {
    this.compareCoursesClicked.emit();
  }
}
